import { Card, DatePicker, Divider, Flex, Typography } from 'antd'
import { Dayjs } from 'dayjs'
import { FC } from 'react'
import styled from 'styled-components'
import SummaryStatCard from '../../printer-content/components/summary-stat-card'
import { DESIGNER_SUMMARY_LABELS } from '../constants/designer-summary-labels'
import useDesignerCompletedTasks from '../hooks/use-designer-completed-tasks'
import BonusRatesHint from './bonus-rates-hint'
import TasksTable from './tasks-table'

const SectionTitle = styled(Typography.Title)`
    margin-top: 0 !important;
    margin-bottom: 16px !important;
`

const MonthPickerRow = styled(Flex)`
    margin-bottom: 16px;
`

const StatsRow = styled(Flex)`
    margin-bottom: 16px;
`

const MetricCard = styled.div`
    flex: 1;
    min-width: 160px;
`

const TotalCard = styled(Card)`
    flex: 0 0 200px;
    min-width: 180px;
    border-color: ${p => p.theme.colorSuccessBorder};
    background: ${p => p.theme.colorSuccessBg};

    & .ant-card-body {
        padding: 12px 16px;
    }
`

const TotalLabel = styled(Typography.Text)`
    display: block;
    margin-bottom: 4px;
    font-size: 12px;
`

const TotalValue = styled(Typography.Text)`
    font-size: 22px;
    line-height: 1.2;
`

const ContentRow = styled(Flex)`
    min-height: 0;
`

const RatesColumn = styled.div`
    flex: 0 0 auto;
`

const TableColumn = styled.div`
    flex: 1;
    min-width: 0;
`

interface DesignerMonthlySummaryProps {
    month: Dayjs
    onMonthChange: (month: Dayjs) => void
}

const DesignerMonthlySummary: FC<DesignerMonthlySummaryProps> = ({ month, onMonthChange }) => {
    const { rows, isLoading, stats } = useDesignerCompletedTasks(month)

    return (
        <div>
            <SectionTitle level={4}>Сводка за месяц</SectionTitle>

            <MonthPickerRow align='center' gap={12} wrap='wrap'>
                <Typography.Text style={{ width: 60 }}>Месяц:</Typography.Text>
                <DatePicker
                    picker='month'
                    value={month}
                    onChange={value => value && onMonthChange(value)}
                    allowClear={false}
                    style={{ width: 140 }}
                />
                <Typography.Text>
                    {DESIGNER_SUMMARY_LABELS.formsOrdered}:{' '}
                    <Typography.Text strong>{stats.formsOrdered ?? '—'}</Typography.Text>
                </Typography.Text>
                <Typography.Text>
                    {DESIGNER_SUMMARY_LABELS.digitalDismountings}:{' '}
                    <Typography.Text strong>{stats.digitalDismountings ?? '—'}</Typography.Text>
                </Typography.Text>
                <Typography.Text>
                    {DESIGNER_SUMMARY_LABELS.stampsOrdered}:{' '}
                    <Typography.Text strong>{stats.stampsOrdered ?? '—'}</Typography.Text>
                </Typography.Text>
                <Typography.Text>
                    {DESIGNER_SUMMARY_LABELS.formsTotal}:{' '}
                    <Typography.Text strong>{stats.formsTotal ?? '—'}</Typography.Text>
                </Typography.Text>
            </MonthPickerRow>

            <StatsRow align='stretch' gap={12} wrap='wrap'>
                <MetricCard>
                    <SummaryStatCard
                        label={DESIGNER_SUMMARY_LABELS.flexo}
                        value={stats.flexoBonus}
                        tone={stats.flexoBonus != null ? 'success' : null}
                        details={[
                            `${DESIGNER_SUMMARY_LABELS.flexoCount}: ${stats.flexoCount ?? '—'}`,
                        ]}
                    />
                </MetricCard>
                <MetricCard>
                    <SummaryStatCard
                        label={DESIGNER_SUMMARY_LABELS.digital}
                        value={stats.digitalBonus}
                        tone={stats.digitalBonus != null ? 'success' : null}
                        details={[
                            `${DESIGNER_SUMMARY_LABELS.digitalDismountings}: ${
                                stats.digitalDismountings ?? '—'
                            }`,
                        ]}
                    />
                </MetricCard>
                <MetricCard>
                    <SummaryStatCard
                        label={DESIGNER_SUMMARY_LABELS.overtime}
                        value={stats.overtimeBonus}
                        tone={stats.overtimeBonus != null ? 'success' : null}
                        details={[
                            `${DESIGNER_SUMMARY_LABELS.overtimeHours}: ${
                                stats.overtimeHours ?? '—'
                            }`,
                        ]}
                    />
                </MetricCard>

                <Divider type='vertical' style={{ height: 'auto', margin: '0 4px' }} />

                <TotalCard size='small'>
                    <TotalLabel type='secondary'>{DESIGNER_SUMMARY_LABELS.total}</TotalLabel>
                    <TotalValue strong type={stats.totalBonus != null ? 'success' : undefined}>
                        {stats.totalBonus ?? '—'}
                    </TotalValue>
                </TotalCard>
            </StatsRow>

            <ContentRow gap={24} align='flex-start'>
                <RatesColumn>
                    <BonusRatesHint flexoRate={stats.flexoRate} digitalRate={stats.digitalRate} />
                </RatesColumn>
                <TableColumn>
                    <TasksTable rows={rows} isLoading={isLoading} />
                </TableColumn>
            </ContentRow>
        </div>
    )
}

export default DesignerMonthlySummary
